import { createAsyncThunk } from '@reduxjs/toolkit'
import { AWSFile } from '../models/File'
import { setIsLoading, updateDrawingFile } from '../actions/files'
import { RootState } from '../store'
import { getIDToken } from '../utils/AuthUtil'
import { BOOK_PROCESSOR_BASE_URL } from '../config'
import {
  setBookExtractPage,
  setChapterList,
  setCodeRules,
  setRuleList,
  updateSelectedChapter,
} from '../actions/rule'

export const analyzeBookFilesAsync = createAsyncThunk(
  'rules/analyzeExtractFilesAsync',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const bucket = state.drawings.files[0].s3Bucket
    const files = state.drawings.files.map((file) => file.s3Filename)

    // const queryString = `bucket=${bucket}&files=${files}`
    const url = `${BOOK_PROCESSOR_BASE_URL}/api/processors/books`
    const s3FileKey = files[0]
    const splittedS3FIleKey = s3FileKey?.split('/') || []
    const fileName = splittedS3FIleKey[splittedS3FIleKey.length - 1]
    const requestBody = {
      input_source: fileName,
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
      })
      .then((data) => {
        const chapters: any = {}
        data?.flat_rules
          ?.filter((rule: any) => rule.chapter_name)
          .map((rule: any) => {
            let chapter = chapters[rule.chapter_name] || {}
            let sections = chapter[rule.section_name] || []
            sections.push(rule)
            chapter[rule.section_name] = sections
            chapters[rule.chapter_name] = chapter
          })
        const chapterList = Object.keys(chapters).map((chapter_name) => {
          const sections = chapters[chapter_name]
          const sectionList = Object.keys(sections).map((section_name) => {
            return {
              name: section_name,
              rules: sections[section_name],
            }
          })
          return {
            name: chapter_name,
            sections: sectionList,
          }
        })
        dispatch(setChapterList(chapterList))
        dispatch(setIsLoading(false))
      })
      .catch((error) => {
        console.error('Error during files analysis: ', error)
      })
  },
)

export const analyzeCodeAsync = createAsyncThunk(
  'rules/analyzeCodeAsync',
  async (
    payload: { projectName: string; ruleId: string },
    { dispatch, getState },
  ) => {
    // const queryString = `bucket=${bucket}&files=${files}`
    const url = `${BOOK_PROCESSOR_BASE_URL}/api/processors/rules`
    const requestBody = {
      project_name: payload.projectName,
      code_regulation_id: payload.ruleId,
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
      })
      .then((data) => {
        dispatch(setCodeRules(data.data))
        dispatch(setRuleList(data?.data?.table_output.rules))
        dispatch(setBookExtractPage('ANALYZE_CODE'))
        dispatch(setIsLoading(false))
      })
      .catch((error) => {
        console.error('Error during files analysis: ', error)
      })
  },
)

export const uploadExtractFilesAsync = createAsyncThunk(
  'files/uploadExtractFilesAsync',
  async (
    payload: { files: { id: string; source: File }[] },
    { dispatch, getState },
  ) => {
    const file = payload.files[0].source
    let signedUrl: AWSFile
    try {
      const state = getState() as RootState
      const s3Foldername = state.drawings.selecetedS3Folder
      signedUrl = (await dispatch(
        getExtractSignedUrl({ filename: file.name, foldername: s3Foldername }),
      ).unwrap()) as AWSFile
    } catch (error) {
      console.error(error)
      return
    }

    fetch(signedUrl.s3_filename, {
      method: 'PUT',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': file.type,
      },
      body: file,
    })
      .then((_) => {
        const state = getState() as RootState
        const existingFile = state.drawings.files.find(
          (file) => file.id === payload.files[0].id,
        )
        const url = new URL(signedUrl.s3_filename)
        dispatch(
          updateDrawingFile({
            fileId: payload.files[0].id,
            newFile: {
              ...existingFile!,
              s3Bucket: signedUrl.s3_bucketname,
              s3Folder: signedUrl.s3_folder,
              s3Filename: decodeURIComponent(url.pathname.replace(/^\/+/, '')),
            },
          }),
        )
      })
      .catch((error) => console.error('Error during uploading files: ', error))
  },
)

const getExtractSignedUrl = createAsyncThunk(
  'files/getExtractSignedUrlAsync',
  async (payload: { filename: string; foldername?: string }) => {
    const response = await fetch(
      `${BOOK_PROCESSOR_BASE_URL}/api/processors/getSignedUrl`,
      {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: payload.filename,
          s3_folder: payload.foldername ? payload.foldername : null,
        }),
      },
    )

    if (!response.ok) {
      throw new Error('Failed to get signed URL')
    }

    const data = await response.json()

    return data
  },
)

export const analyzeExtractFilesAsync = createAsyncThunk(
  'files/analyzeExtractFilesAsync',
  async (payload: { fileName: string }, { dispatch }) => {
    const url = `${BOOK_PROCESSOR_BASE_URL}/api/processors/books`

    const requestBody = {
      input_source: payload.fileName,
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
      })
      .then((data) => {
        const chapters: any = {}
        data?.flat_rules
          ?.filter((rule: any) => rule.chapter_name)
          .map((rule: any) => {
            let chapter = chapters[rule.chapter_name] || {}
            let sections = chapter[rule.section_name] || []
            sections.push(rule)
            chapter[rule.section_name] = sections
            chapters[rule.chapter_name] = chapter
          })
        const chapterList = Object.keys(chapters).map((chapter_name) => {
          const sections = chapters[chapter_name]
          const sectionList = Object.keys(sections).map((section_name) => {
            return {
              name: section_name,
              rules: sections[section_name],
            }
          })
          return {
            name: chapter_name,
            sections: sectionList,
          }
        })
        dispatch(setChapterList(chapterList))
        if (chapterList?.length > 0)
          dispatch(updateSelectedChapter(chapterList[0]))
        dispatch(setBookExtractPage('EXTRACT_CODE'))
        dispatch(setIsLoading(false))
      })
      .catch((error) => {
        console.error('Error during files analysis: ', error)
      })
  },
)
